import { Button, ButtonGroup, Heading, IconButton, Input, LuIcon, Menu, Separator } from "@overmap-ai/blocks"
import { type Form, type Stored } from "@overmap-ai/common"
import {
	selectFormIdentifiers,
	selectFormRevisions,
	selectFormsOfOrganization,
	selectFormSubmissionsOfProject,
	selectUsersOfOrganization,
} from "@overmap-ai/core"
import { useTextFilter } from "@overmap-ai/hooks"
import { getRouteApi, useNavigate } from "@tanstack/react-router"
import { memo, useCallback, useMemo, useState } from "react"

import { ViewContainer } from "../../components/Containers/ViewContainer"
import { ExportFormSubmissionsCsvDialog } from "../../components/Dialogs/ExportFormSubmissionsCsvDialog"
import { FormFilterMenu } from "../../components/FilterMenus/FormFilterMenu"
import { useFormTable, useFormTableColumns } from "../../components/Tables"
import { ColumnVisibilityDropdown } from "../../components/Tables/components"
import { tableIconMapping } from "../../components/Tables/mappings"
import { Table } from "../../components/Tables/Table"
import { FilterRenderer } from "../../filtering/components"
import type { AnyFilter } from "../../filtering/filter"
import { useFilterManager, useFilters } from "../../filtering/hooks"
import { getFormFilterSchema } from "../../filtering/schemas"
import { formFilterCallback } from "../../filtering/utils"
import { useChangeEventHandler } from "../../hooks/utils"
import { modelIconMapping } from "../../mappings/icons"
import { setFormFilters, useAppDispatch, useAppSelector, useShallowEqaulSelector } from "../../store"
import { selectFormFilters } from "../../store/selectors"

const routeApi = getRouteApi("/_loggedIn/$organizationId/_map/_layout/$projectId/_projectMap/forms/")

export const ProjectFormsView = memo(() => {
	const { organizationId, projectId } = routeApi.useParams()

	const forms = useShallowEqaulSelector(selectFormsOfOrganization(organizationId))
	const users = useShallowEqaulSelector(selectUsersOfOrganization(organizationId))
	const formSubmissions = useShallowEqaulSelector(selectFormSubmissionsOfProject(projectId))
	const formRevisions = useShallowEqaulSelector(selectFormRevisions)
	const formIdentifiers = useShallowEqaulSelector(selectFormIdentifiers)
	const columns = useFormTableColumns({ formSubmissions })
	const navigate = useNavigate()
	const dispatch = useAppDispatch()
	const [showExportCsvDialog, setShowExportCsvDialog] = useState(false)
	const serializedFilters = useAppSelector(selectFormFilters)
	const filterSchema = useMemo(() => getFormFilterSchema({ users }), [users])

	const filters = useMemo(() => {
		return filterSchema.deserializeFilters(serializedFilters)
	}, [serializedFilters, filterSchema])

	const handleFiltersChange = useCallback(
		(filters: AnyFilter[]) => {
			dispatch(setFormFilters(filterSchema.serializeFilters(filters)))
		},
		[dispatch, filterSchema],
	)

	const filterManager = useFilterManager({
		filterSchema,
		filters,
		onFilterChange: handleFiltersChange,
	})

	const filteredForms = useFilters({
		filters: filters,
		data: forms,
		filterManager: filterManager,
	})

	const [searchedForms, filterValue, setFilterValue] = useTextFilter(filteredForms, formFilterCallback)

	const table = useFormTable({
		columns,
		data: searchedForms,
		enableHiding: true,
		enableRowSelection: true,
	})

	const selectedForms = table.getSelectedRowModel().rows.map((row) => row.original)
	const selectedRowCount = selectedForms.length

	const handleFormTableRowClick = useCallback(
		(form: Stored<Form>) => {
			void navigate({
				from: "/$organizationId/$projectId/forms/",
				to: "/$organizationId/$projectId/forms/$formId",
				params: (prev) => ({
					...prev,
					formId: form.uuid,
				}),
			})
		},
		[navigate],
	)

	return (
		<ViewContainer maxWidth="6xl">
			<div className="flex size-full flex-col gap-4">
				<div className="flex h-max w-full flex-col gap-1">
					<div className="flex items-end justify-between gap-2">
						<Heading size="xl">Project Forms</Heading>
					</div>
				</div>

				<Separator size="full" />

				<div className="flex max-h-full w-full flex-col gap-2 overflow-hidden">
					<ButtonGroup size="sm" variant="soft" accentColor="base" className="flex justify-between gap-2">
						<div className="flex grow items-center gap-2">
							<Input.Root variant="outline">
								<Input.Slot>
									<LuIcon icon={modelIconMapping.search} />
								</Input.Slot>
								<Input.Field
									placeholder="Search"
									value={filterValue}
									onChange={useChangeEventHandler(setFilterValue)}
								/>
							</Input.Root>
							<FormFilterMenu filterManager={filterManager} users={users} align="start" size="sm">
								<Button>
									<LuIcon icon={modelIconMapping.filter} />
									Filter
								</Button>
							</FormFilterMenu>

							{filterManager.getFilters().length > 0 && (
								<IconButton onClick={filterManager.removeAll}>
									<LuIcon icon={modelIconMapping.close} />
								</IconButton>
							)}
						</div>

						<div className="flex grow items-center justify-end gap-2">
							{selectedRowCount > 0 && (
								<Menu.Root>
									<Menu.ClickTrigger>
										<Button>
											<LuIcon icon={modelIconMapping.command} />
											Actions
										</Button>
									</Menu.ClickTrigger>
									<Menu.Content size="sm">
										<Menu.Item onSelect={() => setShowExportCsvDialog(true)}>
											<LuIcon icon={modelIconMapping.download} />
											Export CSV
										</Menu.Item>
									</Menu.Content>
								</Menu.Root>
							)}
							<ColumnVisibilityDropdown table={{ ...table }} align="end" size="sm">
								<IconButton>
									<LuIcon icon={tableIconMapping.columns} />
								</IconButton>
							</ColumnVisibilityDropdown>
						</div>
					</ButtonGroup>

					<FilterRenderer filterManager={filterManager} size="sm" />

					<div className="max-h-full w-full overflow-auto">
						<Table
							className="w-full"
							table={{ ...table }}
							onRowClick={handleFormTableRowClick}
							variant="surface"
							size="sm"
							interactive
							emptyText="No forms."
						/>
					</div>
				</div>
			</div>

			<ExportFormSubmissionsCsvDialog
				open={showExportCsvDialog}
				onOpenChange={setShowExportCsvDialog}
				forms={selectedForms}
				formSubmissions={formSubmissions}
				formRevisions={formRevisions}
				formIdentifiers={formIdentifiers}
				users={users}
				size="sm"
			/>
		</ViewContainer>
	)
})
ProjectFormsView.displayName = "ProjectFormsView"
